import React from 'react';
import PropTypes from 'prop-types';
import { SFEntityID } from 'utils/types';
import { hot } from 'react-hot-loader';
import { Tab, TabList, TabPanel } from 'react-tabs';
import SFTab from './SFTab';
import css from './App.css';
import ProgressSurveys from 'progressSurveys/App';
import { SFSurveysTenantLanguage } from "utils/types";
import BatchActionNotification from "../studentList/BatchActionNotification";
import BulkActionCancelToast from '../studentList/BulkActionCancelToast';
import { NotificationCenter } from 'utils/types';
import AccessDenied from 'common/AccessDenied';
import AccessibleTabs from 'common/AccessibleTabs';

const LOCAL_STORAGE_KEY = 'userLastStudentActiveTab';

class App extends React.Component {

  constructor(props) {
    super(props);
    const { tabs, userId } = props;
    const { selectedIndex, showSurveyErrorMessage } = this.getInitialValues(tabs, userId);
    this.state = { 
      surveyCount: this.props.surveyCount,
      selectedIndex, 
      showSurveyErrorMessage,
    };
    this.batchNotification = React.createRef();
    this.notificationShow = false;
  }

  componentDidMount() {
    // We use this little trick to guarantee the elements are mounted when we try to scroll
    setTimeout(() => this.selectTab(this.state.selectedIndex), 0);
  }
  
  updateSurveyCount(surveyCount) {
    this.setState({surveyCount});
  }

  getTabIndex(tabs, tabQueryParam) {
    return tabs.findIndex(({queryParam}) => queryParam === tabQueryParam);
  }

  get lastTabDict() {
    return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)) || {};
  }

  getInitialValues(tabs, userId) {
    const searchParams = new URLSearchParams(window.location.search);
    const param = searchParams.get('tabRequest');
    
    // Find the tab object which has the same queryParam attribute as the param we just read from the URL
    const index = this.getTabIndex(tabs, param);
    
    // If none found, look for the last active tab in the local storage
    const selectedIndex = index === -1 ? this.getLastActiveTabIndex(tabs, userId) : index;

    // If user hit the surveys tab but it isn't available, show error message
    const showSurveyErrorMessage = param === "flagSurveysTab" && index === -1;

    return { selectedIndex, showSurveyErrorMessage };
  }

  getLastActiveTabIndex(tabs, userId) {
    // Load last tab dictionary from local storage and get the entry for the current user
    const lastUserTab = this.lastTabDict[userId];

    // If no tab previously saved, defaults to the first one
    if ( !lastUserTab ) return 0; 

    // Get index of last saved tab from tabs array or default to the first one if not found
    const index = this.getTabIndex(tabs, lastUserTab);
    return index === -1 ? 0 : index;
  }

  saveTabToLocalStorage(index) {
    // Get query param name for the given index
    const { queryParam } = this.props.tabs[index];
    if ( !queryParam ) return; // This shouldn't happen

    // Get tab dict and update it
    const newDict = Object.assign({}, this.lastTabDict, { [this.props.userId]: queryParam });

    // Save updated dict to local storage
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newDict));
  }

  updateQueryParam(index) {
    const { queryParam } = this.props.tabs[index];

    // Use the already polyfilled URLSearchParams API
    // to generate the new query params
    // Reference: https://stackoverflow.com/a/41662783/4487722
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('tabRequest', queryParam);

    // Replace the URL without triggering a refresh
    // Reference https://stackoverflow.com/a/19279428/4487722
    history.replaceState(null, '', `?${searchParams.toString()}${window.location.hash}`);
  }

  selectTab(index) {
    // If batchActionNotification has been rendered, save its display state
    if (this.batchNotification.current) {
      this.notificationShow = this.batchNotification.current.state.display;
    }

    this.setState({selectedIndex: index});
    this.updateQueryParam(index);
    this.saveTabToLocalStorage(index);
    this.focusOnTab(index);
  }

  focusOnTab(index) {
    // Fallback if ref for some reason is undefined or null
    if ( !this.tabListRef ) return; 

    // The target element is the one we are selecting         
    const element = this.tabListRef.children[index];

    // Fallback if something unexpected happens
    if ( !element ) return;

    const { offsetWidth, offsetLeft } = element;
    const { innerWidth } = window;

    element.focus();

    // We try to centralize the element if possible. Then we define
    // the internal offset in regard to the viewport
    const internalOffset = (innerWidth - offsetWidth) / 2;

    // The new scroll position when added up with the internal offset
    // must be the total distance from the element to the leftmost point in
    // the page
    const newScroll = offsetLeft - internalOffset;
    this.tabListRef.scrollLeft = newScroll;
  }

  renderTabContent(render, queryParam) {
    if (queryParam === "flagSurveysTab") return (
      <ProgressSurveys
        getStudentFolderLink={this.props.getStudentFolderLink}
        rebindStudentCards={this.props.rebindStudentCards}
        updateSurveyCount={this.updateSurveyCount.bind(this)}
        progressSurveyWords={this.props.progressSurveyWords}
      />
    );

    if (sfTenant.getTenantConfig('BATCH_ACTION_PROCESSING_ENABLED') 
      && (queryParam === 'allStudentsTab' || queryParam === 'interventionTab' || queryParam === 'intakeTab')) {
      return (
        <div>
          <BatchActionNotification ref={this.batchNotification} display={this.notificationShow} notificationCenter={this.props.notificationCenter}/>
          <SFTab renderComponent={render}/>
          <BulkActionCancelToast          
          notificationCenter={this.props.notificationCenter}
          />
        </div>
      )
    }

    return <SFTab renderComponent={render} />;
  }
  
  renderTabTitle(title, queryParam) {
    const { surveyCount } = this.state;
    if(queryParam === "flagSurveysTab" && surveyCount > 0) return `${title} (${surveyCount})`;
    return title;
  }

  render() {
    if ( this.state.showSurveyErrorMessage ) return (
      <AccessDenied />
    );
    return (
      <AccessibleTabs
        domRef={node => { if ( node ) this.tabListRef = node.firstChild; }}
        className={css['container']}
        selectedTabClassName={css['tab-selected']}
        selectedTabPanelClassName={css['panel']}
        onSelect={this.selectTab.bind(this)}
        selectedIndex={this.state.selectedIndex}>
        <TabList className={css['list-container']}>
          {this.props.tabs
            .map(({title, queryParam}, i) => 
              <Tab key={i}>{this.renderTabTitle(title, queryParam)}</Tab>
          )}
        </TabList>
        {this.props.tabs
          .map(({render, queryParam}, i) => 
            <TabPanel key={i}>{this.renderTabContent(render, queryParam)}</TabPanel>
        )}
      </AccessibleTabs>
    );
  }
}

App.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired,
    queryParam: PropTypes.string.isRequired,
    visible: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
    render: PropTypes.func
  })),
  userId: SFEntityID.isRequired,
  getStudentFolderLink: PropTypes.func.isRequired,
  rebindStudentCards: PropTypes.func.isRequired,
  surveyCount: PropTypes.number,
  progressSurveyWords: SFSurveysTenantLanguage,
  notificationCenter: NotificationCenter
};

export default hot(module)(App);
